import { useCallback, useRef, useState } from 'react';
import { CaptchaProps, CaptchaRef } from '../types/captcha.type';

type UseCaptchaProps = {
  siteKey: string;
  type: CaptchaProps['type'];
};

type UseCaptchaReturn = {
  code: string;
  reset: () => void;
  execute: () => Promise<string | undefined>;
  ref: React.RefObject<CaptchaRef | null>;
  captcha: {
    ref: React.RefObject<CaptchaRef | null>;
    type: CaptchaProps['type'];
    siteKey: string;
    onVerify: (code: string) => void;
    onExpired: () => void;
    onError: () => void;
  };
};

export const useCaptcha = ({ siteKey, type }: UseCaptchaProps): UseCaptchaReturn => {
  const [code, setCode] = useState('');
  const ref = useRef<CaptchaRef>(null);

  const onVerify = useCallback((token: string) => {
    setCode(token);
  }, []);

  const onExpired = useCallback(() => {
    setCode('');
  }, []);

  const onError = useCallback(() => {
    setCode('');
  }, []);

  const reset = useCallback(() => {
    setCode('');
  }, []);

  const execute = useCallback(async () => {
    const token = await ref.current?.execute();
    if (token) setCode(token);
    return token;
  }, []);

  return {
    code,
    reset,
    execute,
    ref,
    captcha: {
      ref,
      type,
      siteKey,
      onVerify,
      onExpired,
      onError,
    },
  };
};
